import React, { useState } from 'react';
import DatePicker from '../components/DatePicker';
import { reportsAPI } from '../services/api';
import { formatDate, getTodayKualaLumpurISODate, toKualaLumpurISODate } from '../utils/dateFormat';
import { useLocalization } from '../context/LocalizationContext';
import styles from '../styles/ReportPages.module.css';

const fmtRM = (val) => `RM ${Number(val || 0).toLocaleString('en-MY', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

const formatMemberWithCost = (member) => {
  if (!member) return '-';
  const name = member.username || member.full_name || '-';
  if (member.member_cost == null) return name;
  return `${name} (${fmtRM(member.member_cost)})`;
};

const formatResourceWithCost = (item) => {
  if (!item) return '-';
  const name = item.resource_name || '-';
  const qty = Number(item.quantity || 0);
  if (item.item_cost == null || item.unit_cost == null) {
    return `${name} x${qty}`;
  }
  return `${name} x${qty} (${fmtRM(item.item_cost)})`;
};

const downloadCsv = (filename, rows) => {
  const escapeCell = (value) => `"${String(value ?? '').replaceAll('"', '""')}"`;
  const csv = rows.map((row) => row.map(escapeCell).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};

const TaskReportPage = () => {
  const { t } = useLocalization();
  const today = getTodayKualaLumpurISODate();
  const [mode, setMode] = useState('daily');
  const [reportType, setReportType] = useState('summary');
  const [status, setStatus] = useState('');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');

  const handleModeChange = (nextMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    if (nextMode === 'daily') {
      const day = getTodayKualaLumpurISODate();
      setStartDate(day);
      setEndDate(day);
    } else if (nextMode === 'monthly') {
      setStartDate(toKualaLumpurISODate(new Date(year, month, 1)));
      setEndDate(toKualaLumpurISODate(new Date(year, month + 1, 0)));
    } else if (nextMode === 'yearly') {
      setStartDate(toKualaLumpurISODate(new Date(year, 0, 1)));
      setEndDate(toKualaLumpurISODate(new Date(year, 11, 31)));
    }
    // 'range' keeps current dates
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError('');
      const params = { mode, startDate, endDate, ...(status ? { status } : {}) };
      const response = reportType === 'summary'
        ? await reportsAPI.taskSummaryReport(params)
        : await reportsAPI.taskDetailReport(params);
      setRows(response.data?.data || []);
    } catch (e) {
      setError(e.response?.data?.message || t('task_report_load_failed', 'Failed to load task report'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (reportType === 'summary') {
      const csvRows = [
        [
          t('task_report_task_date', 'Task Date'),
          t('reports_task', 'Task'),
          t('tasks_label_status', 'Status'),
          t('tasks_label_priority', 'Priority'),
          t('tasks_label_job_type', 'Job Type'),
          t('tasks_label_team', 'Team'),
          t('task_report_revenue_rm', 'Revenue (RM)'),
          t('task_report_total_cost_rm', 'Total Cost (RM)'),
          t('task_report_profit_rm', 'Profit (RM)')
        ],
        ...rows.map((row) => [
          row.task_date,
          row.title,
          row.status,
          row.priority,
          row.job_type_name || '-',
          row.team_name || '-',
          Number(row.revenue_amount || 0).toFixed(2),
          Number(row.total_cost || 0).toFixed(2),
          Number(row.profit_amount || 0).toFixed(2),
        ]),
      ];
      downloadCsv(t('task_report_summary_export_filename', 'task-summary-report.csv'), csvRows);
      return;
    }

    const csvRows = [
      [
        t('task_report_task_date', 'Task Date'),
        t('reports_task', 'Task'),
        t('tasks_label_status', 'Status'),
        t('tasks_label_team', 'Team'),
        t('task_report_team_members_cost', 'Team Members + Cost'),
        t('task_report_resources_cost', 'Resources + Cost'),
        t('task_report_revenue_rm', 'Revenue (RM)'),
        t('task_report_total_cost_rm', 'Total Cost (RM)'),
        t('task_report_profit_rm', 'Profit (RM)'),
        t('task_report_time_taken_h', 'Time Taken (h)'),
        t('task_report_status_transition_durations', 'Status Transition Durations')
      ],
      ...rows.map((row) => [
        row.task_date,
        row.title,
        row.status,
        row.team_name || '-',
        Array.isArray(row.team_members) && row.team_members.length > 0
          ? row.team_members.map(formatMemberWithCost).join(' | ')
          : '-',
        Array.isArray(row.resource_items) && row.resource_items.length > 0
          ? row.resource_items.map(formatResourceWithCost).join(' | ')
          : '-',
        Number(row.revenue_amount || 0).toFixed(2),
        Number(row.total_cost || 0).toFixed(2),
        Number(row.profit_amount || 0).toFixed(2),
        row.time_taken_hours ?? '-',
        Array.isArray(row.status_durations) ? row.status_durations.join(' | ') : '-',
      ]),
    ];
    downloadCsv(t('task_report_detail_export_filename', 'task-detail-report.csv'), csvRows);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>{t('task_report_title', 'Task Report')}</h1>
        <p>{t('task_report_subtitle', 'Filter by status, period, and generate summary/detail reports.')}</p>
      </div>

      <div className={styles.controls}>
        <div className={styles.modeButtons}>
          {['daily', 'monthly', 'yearly', 'range'].map((item) => (
            <button
              key={item}
              type="button"
              className={`${styles.modeBtn} ${mode === item ? styles.modeBtnActive : ''}`}
              onClick={() => handleModeChange(item)}
            >
              {item === 'daily'
                ? t('common_daily', 'Daily')
                : item === 'monthly'
                  ? t('common_monthly', 'Monthly')
                  : item === 'yearly'
                    ? t('common_yearly', 'Yearly')
                    : t('common_range', 'Range')}
            </button>
          ))}
        </div>

        <select className={styles.select} value={reportType} onChange={(e) => setReportType(e.target.value)}>
          <option value="summary">{t('reports_summary', 'Summary')}</option>
          <option value="detail">{t('reports_detail', 'Detail')}</option>
        </select>

        <select className={styles.select} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">{t('reports_all_status', 'All Status')}</option>
          <option value="pending">{t('tasks_status_pending', 'Pending')}</option>
          <option value="in_progress">{t('tasks_status_in_progress', 'In Progress')}</option>
          <option value="completed">{t('tasks_status_completed', 'Completed')}</option>
          <option value="cancelled">{t('tasks_status_cancelled', 'Cancelled')}</option>
        </select>

        <DatePicker value={startDate} onChange={mode === 'range' ? setStartDate : undefined} placeholder={t('tasks_start_date', 'Start date')} className={styles.dateInput} disabled={mode !== 'range'} />
        <DatePicker value={endDate} onChange={mode === 'range' ? setEndDate : undefined} placeholder={t('tasks_end_date', 'End date')} className={styles.dateInput} disabled={mode !== 'range'} />

        <button className={styles.actionBtn} type="button" onClick={fetchReport}>{t('reports_view', 'View')}</button>
        <button className={styles.actionBtnSecondary} type="button" onClick={handleExport} disabled={rows.length === 0}>{t('reports_export_excel', 'Export Excel')}</button>
      </div>

      {loading ? (
        <p>{t('task_report_loading', 'Loading task report...')}</p>
      ) : error ? (
        <p className={styles.error}>{error}</p>
      ) : (
        <>
          {reportType === 'summary' ? (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('reports_date', 'Date')}</th>
                    <th>{t('reports_task', 'Task')}</th>
                    <th>{t('tasks_label_status', 'Status')}</th>
                    <th>{t('tasks_label_priority', 'Priority')}</th>
                    <th>{t('tasks_label_team', 'Team')}</th>
                    <th>{t('kanban_financial_revenue', 'Revenue')}</th>
                    <th>{t('reports_total_cost', 'Total Cost')}</th>
                    <th>{t('kanban_financial_profit', 'Profit')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={8} className={styles.empty}>{t('task_report_no_data', 'No task data found.')}</td></tr>
                  ) : rows.map((row) => (
                    <tr key={row.id}>
                      <td>{formatDate(row.task_date)}</td>
                      <td>{row.title}</td>
                      <td><span className={`${styles.statusBadge} ${styles[`status_${row.status}`]}`}>{row.status?.replace('_', ' ')}</span></td>
                      <td><span className={`${styles.priorityBadge} ${styles[`priority_${row.priority}`]}`}>{row.priority}</span></td>
                      <td>{row.team_name || '-'}</td>
                      <td>{fmtRM(row.revenue_amount)}</td>
                      <td>{fmtRM(row.total_cost)}</td>
                      <td>{fmtRM(row.profit_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles.detailCardsWrap}>
              {rows.length === 0 ? (
                <p className={styles.empty}>{t('task_report_no_data', 'No task data found.')}</p>
              ) : rows.map((row) => (
                <div key={row.id} className={styles.detailCard}>
                  <div className={styles.detailCardHeader}>
                    <span className={styles.detailCardTitle}>{row.title}</span>
                    <div className={styles.detailCardBadges}>
                      <span className={`${styles.statusBadge} ${styles[`status_${row.status}`]}`}>{row.status?.replace('_', ' ')}</span>
                      {row.priority && <span className={`${styles.priorityBadge} ${styles[`priority_${row.priority}`]}`}>{row.priority}</span>}
                    </div>
                  </div>
                  <div className={styles.detailCardBody}>
                    <div className={styles.detailCardGrid}>
                      <div className={styles.detailCardField}>
                        <span className={styles.detailCardLabel}>{t('reports_date', 'Date')}</span>
                        <span>{formatDate(row.task_date)}</span>
                      </div>
                      <div className={styles.detailCardField}>
                        <span className={styles.detailCardLabel}>{t('tasks_label_team', 'Team')}</span>
                        <span>{row.team_name || '-'}</span>
                      </div>
                      <div className={styles.detailCardField}>
                        <span className={styles.detailCardLabel}>{t('tasks_label_job_type', 'Job Type')}</span>
                        <span>{row.job_type_name || '-'}</span>
                      </div>
                      <div className={styles.detailCardField}>
                        <span className={styles.detailCardLabel}>{t('task_report_time_taken', 'Time Taken')}</span>
                        <span>{row.time_taken_hours != null ? `${row.time_taken_hours}h` : '-'}</span>
                      </div>
                      <div className={styles.detailCardField}>
                        <span className={styles.detailCardLabel}>{t('kanban_financial_revenue', 'Revenue')}</span>
                        <span>{fmtRM(row.revenue_amount)}</span>
                      </div>
                      <div className={styles.detailCardField}>
                        <span className={styles.detailCardLabel}>{t('reports_total_cost', 'Total Cost')}</span>
                        <span>{fmtRM(row.total_cost)}</span>
                      </div>
                      <div className={styles.detailCardField}>
                        <span className={styles.detailCardLabel}>{t('kanban_financial_profit', 'Profit')}</span>
                        <span>{fmtRM(row.profit_amount)}</span>
                      </div>
                    </div>
                    <div className={styles.userTaskList}>
                      <div className={styles.userTaskListHeader}>{t('task_report_team_members_cost', 'Team Members + Cost')}</div>
                      {Array.isArray(row.team_members) && row.team_members.length > 0 ? (
                        row.team_members.map((member, index) => (
                          <div key={`${row.id}-member-${member.user_id || index}`} className={styles.userTaskRow}>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('reports_user', 'User')}</span>
                              <span className={styles.userTaskValue}>{member.username || '-'}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('users_role', 'Role')}</span>
                              <span className={styles.userTaskValue}>{member.role || '-'}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('task_report_daily_rate_rm', 'Daily Rate (RM)')}</span>
                              <span className={styles.userTaskValue}>{member.daily_rate != null ? fmtRM(member.daily_rate) : '-'}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('task_report_member_cost_rm', 'Member Cost (RM)')}</span>
                              <span className={styles.userTaskValue}>{member.member_cost != null ? fmtRM(member.member_cost) : '-'}</span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className={styles.userNoTasks}>{t('task_report_no_members', 'No team members assigned')}</p>
                      )}
                    </div>

                    <div className={styles.userTaskList}>
                      <div className={styles.userTaskListHeader}>{t('task_report_resources_cost', 'Resources + Cost')}</div>
                      {Array.isArray(row.resource_items) && row.resource_items.length > 0 ? (
                        row.resource_items.map((item, index) => (
                          <div key={`${row.id}-resource-${item.resource_id || index}`} className={styles.userTaskRow}>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('resources_name', 'Resource')}</span>
                              <span className={styles.userTaskValue}>{item.resource_name || '-'}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('resources_quantity', 'Quantity')}</span>
                              <span className={styles.userTaskValue}>{Number(item.quantity || 0)}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('task_report_unit_cost_rm', 'Unit Cost (RM)')}</span>
                              <span className={styles.userTaskValue}>{item.unit_cost != null ? fmtRM(item.unit_cost) : '-'}</span>
                            </div>
                            <div className={styles.userTaskItem}>
                              <span className={styles.userTaskKey}>{t('task_report_resource_cost_rm', 'Resource Cost (RM)')}</span>
                              <span className={styles.userTaskValue}>{item.item_cost != null ? fmtRM(item.item_cost) : '-'}</span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className={styles.userNoTasks}>{t('task_report_no_resources', 'No resources assigned')}</p>
                      )}
                    </div>
                    {Array.isArray(row.status_durations) && row.status_durations.length > 0 && (
                      <div className={styles.detailCardFullRow}>
                        <span className={styles.detailCardLabel}>{t('task_report_status_timeline', 'Status Timeline')}</span>
                        <div className={styles.statusTimeline}>
                          {row.status_durations.map((seg, i) => (
                            <span key={i} className={styles.statusTimelineSeg}>{seg}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TaskReportPage;
